import { DomainVerificationStatus_Enum as DomainVerificationStatus } from '@weave/schema-gen-ts/dist/schemas/email/records/v1/records_service.pb';
import { EmailRecordsV1 } from '@frontend/api-email-records';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';

const WEAVE_NO_REPLY_EMAIL = 'noreply@marketing.sg.weavecoms.com';

type ReturnProps = {
  fromEmail: string;
  fromName: string;
  isLoading: boolean;
  replyToEmail: string;
  replyToName: string;
  warning: string;
};

// TODO: Make this hook multi-location friendly
export const useEmailAddresses = (locationIds: string[]): ReturnProps => {
  const { getLocationName } = useAppScopeStore();
  const { t } = useTranslation('bulk-messaging');
  const locationId = locationIds.length === 1 ? locationIds[0] ?? '' : '';

  const { data, isLoading } = EmailRecordsV1.Queries.useGetLocationPersonalizationQuery(
    { locationId },
    { enabled: !!locationId }
  );

  if (!locationId) {
    return {
      fromEmail: '',
      fromName: '',
      isLoading: false,
      replyToEmail: '',
      replyToName: '',
      warning: '',
    };
  }

  const domain = data?.domain?.domain;
  const hasVerifiedDomain = data?.domain?.verificationStatus === DomainVerificationStatus.VERIFIED;
  const locationName = getLocationName(locationId); // TODO: Use Brand Setting's friendly location name

  const from = data?.locationPersonalization?.from;
  const fromEmail = hasVerifiedDomain && from?.email && domain ? `${from.email}@${domain}` : WEAVE_NO_REPLY_EMAIL;
  const fromName = hasVerifiedDomain && from?.name ? from.name : locationName;

  const replyTo = data?.locationPersonalization?.replyTo;
  const replyToEmail = replyTo?.email && replyTo?.name ? replyTo.email : fromEmail;
  const replyToName = replyTo?.email && replyTo?.name ? replyTo.name : fromName;

  let warning = '';

  if (!isLoading) {
    if (fromEmail === WEAVE_NO_REPLY_EMAIL || replyToEmail === WEAVE_NO_REPLY_EMAIL) {
      warning = t(
        "Your campaign will use Weave's default marketing email address. Consider setting a custom email address for better deliverability and brand recognition."
      );
    } else if (replyTo?.email && !replyTo?.name) {
      warning = t(
        "Your location's reply-to name is missing. This campaign will use the from name and email address for the reply-to."
      );
    } else if (!replyTo?.email && replyTo?.name) {
      warning = t(
        "Your location's reply-to email address is missing. This campaign will use the from name and email address for the reply-to."
      );
    }
  }

  return {
    fromEmail,
    fromName,
    isLoading,
    replyToEmail,
    replyToName,
    warning,
  };
};
